import React, { useCallback, useMemo, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { BtnPrimary } from '../components/common/Buttons';
import { useThemeStore } from '../state/theme';
import { zodResolver } from '@hookform/resolvers/zod';
import { agentSchema, FormInputs } from '../validations/agent';
import { IoAddCircleOutline } from 'react-icons/io5';
import { FaTrashAlt } from 'react-icons/fa';
import PhoneNumberInput from '../components/common/PhoneNumberInput';
import {
  fluencyOptions,
  languageOptions,
  tools,
  channelOptions,
  paymentMethodOptions,
} from '../constants';
import SelectComponent, { SelectOption } from '../components/common/Select';
import { FluencyOption, PaymentMethod } from '../types/agent';
import { OnboardFormProps, PastEmployer } from '../types';
import { addAgent } from '../hooks/mutations';
import { MultiValue, SingleValue } from 'react-select';
import TagInput from '../components/common/TagInput';
import FileUpload from '../components/common/FileUpload';
import { uploadBankStatement } from '../utils/fileUploads';

const calculateTotalExperience = (pastEmployers: any[]): string => {
  if (!pastEmployers || pastEmployers.length === 0) return '0.0';

  let totalMonths = 0;

  pastEmployers.forEach((emp) => {
    // Handle both date string format and year/month format
    let startDate: Date;
    let endDate: Date;

    if (emp.startDate && emp.endDate) {
      // Already in date string format
      startDate = new Date(emp.startDate);
      endDate = new Date(emp.endDate);
    } else if (emp.startYear && emp.startMonth && emp.endYear && emp.endMonth) {
      // Convert from year/month format
      startDate = new Date(
        `${emp.startYear}-${emp.startMonth.padStart(2, '0')}-01`
      );
      endDate = new Date(`${emp.endYear}-${emp.endMonth.padStart(2, '0')}-01`);
    } else {
      return; // Skip if no valid dates
    }

    if (
      !isNaN(startDate.getTime()) &&
      !isNaN(endDate.getTime()) &&
      endDate > startDate
    ) {
      const yearDiff = endDate.getFullYear() - startDate.getFullYear();
      const monthDiff = endDate.getMonth() - startDate.getMonth();
      const dayDiff = endDate.getDate() - startDate.getDate();

      let months = yearDiff * 12 + monthDiff;

      // Add partial month based on days
      if (dayDiff > 0) {
        months += dayDiff / 30;
      } else if (dayDiff < 0) {
        months -= Math.abs(dayDiff) / 30;
      }

      totalMonths += months;
    }
  });

  return (totalMonths / 12).toFixed(1);
};

const formatAddress = (address: any): string => {
  if (!address) return '';
  const city = address.city || '';
  const country = address.country || '';
  return `${city}, ${country}`.replace(/^, |, $/, '');
};

const transformPastEmployers = (pastEmployers: any[]): PastEmployer[] => {
  if (!pastEmployers) return [];

  return pastEmployers.map((emp) => ({
    employerName: emp.employerName || emp.companyName || '',
    employerUrl: emp.employerUrl || '',
    startDate:
      emp.startYear && emp.startMonth
        ? new Date(`${emp.startYear}-${emp.startMonth.padStart(2, '0')}-01`)
            .toISOString()
            .split('T')[0]
        : '',
    endDate:
      emp.endYear && emp.endMonth
        ? new Date(`${emp.endYear}-${emp.endMonth.padStart(2, '0')}-01`)
            .toISOString()
            .split('T')[0]
        : '',
  }));
};

const OnboardForm: React.FC<OnboardFormProps> = ({
  onSuccess,
  onError,
  agent,
}) => {
  const { mutate: addAgentMutation, isPending: isAddingAgent } = addAgent({
    onSuccess: (data) => onSuccess?.(data),
    onError: (e) => onError?.(e),
  });

  const { isDarkMode } = useThemeStore();

  const {
    email,
    personalInfo,
    workExperience,
    contactInfo,
    questionnaire,
    industry,
    relevantIndustries,
  } = agent;

  // Calculate default values
  const defaultValues = useMemo(
    () => ({
      name: personalInfo
        ? `${personalInfo.firstName} ${personalInfo.middleName || ''} ${personalInfo.lastName}`.trim()
        : '',
      profileUri: personalInfo?.profilePic || '',
      dob: personalInfo?.dob
        ? new Date(personalInfo.dob).toISOString().split('T')[0]
        : undefined,
      resumeUri: workExperience?.resume || '',
      applicationVideoUri: questionnaire?.applicationVideo || '',
      personalEmail: email || '',
      whatsappNumber: contactInfo?.wa
        ? {
            number: contactInfo.wa.number,
            countryCode: contactInfo.wa.code,
            countryCodeEnum: contactInfo.wa.countryEnum,
          }
        : undefined,
      languages: questionnaire?.languages || [],
      pastEmployers: transformPastEmployers(
        workExperience?.pastEmployers || []
      ),
      totalExperience: calculateTotalExperience(
        workExperience?.pastEmployers || []
      ),
      currentAddress: formatAddress(contactInfo?.address),
      permanentAddress: undefined,
      tools: agent.metadata?.tools || [],
      channels: agent.metadata?.channels || [],
      skills: [],
      hobbies: [],
      bankDetails: {
        bankStatement: '',
      },
    }),
    [agent]
  );

  const form = useForm<FormInputs>({
    resolver: zodResolver(agentSchema),
    defaultValues,
  });

  const {
    register,
    formState: { errors },
  } = form;

  // Bank statement file upload state
  const [bankStatementFiles, setBankStatementFiles] = useState<File[]>([]);
  const [isUploadingBankStatement, setIsUploadingBankStatement] =
    useState(false);

  // Field arrays
  const languageFields = useFieldArray({
    control: form.control,
    name: 'languages',
  });

  const pastEmployersFields = useFieldArray({
    control: form.control,
    name: 'pastEmployers',
  });

  // Form submission
  const onSubmit = (data: FormInputs) => {
    if (!data.bankDetails?.bankStatement) {
      form.setError('bankDetails.bankStatement', {
        type: 'manual',
        message: 'Bank statement is required',
      });
      return;
    }

    const formData = {
      ...data,
      dob: data.dob || undefined,
      industry: industry || '',
      relevantIndustries: relevantIndustries || [],
    };
    addAgentMutation(formData);
  };

  // Styling
  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const labelClassName = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  const skillTags = form.watch('skills');
  const hobbyTags = form.watch('hobbies');

  const handleTagInput = useCallback(
    (fieldName: 'skills' | 'hobbies') =>
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        const input = e.target as HTMLInputElement;
        const tag = input.value.trim();

        if (tag) {
          const currentTags = form.getValues(fieldName) || [];
          form.setValue(fieldName, [...currentTags, tag]);
          input.value = '';
        }
      },
    [form]
  );

  const handleRemoveTag = useCallback(
    (fieldName: 'skills' | 'hobbies') => (index: number) => {
      const tags = form.getValues(fieldName) || [];
      form.setValue(
        fieldName,
        tags.filter((_: any, i: number) => i !== index)
      );
    },
    [form]
  );

  const handleBankStatementUpload = useCallback(
    async (files: File[]) => {
      await uploadBankStatement(
        files,
        form.setValue,
        setBankStatementFiles,
        setIsUploadingBankStatement
      );
      // Clear the "required" error once the upload sets the key.
      if (form.getValues('bankDetails.bankStatement')) {
        form.clearErrors('bankDetails.bankStatement');
      }
    },
    [form]
  );

  const toolsAndChannels = agent.metadata?.tools || agent.metadata?.channels;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label htmlFor="name" className={labelClassName}>
          Name (As per Legal Documents)
        </label>
        <input
          id="name"
          type="text"
          {...register('name')}
          className={inputClassName}
        />
        {errors.name && (
          <span className="text-red-500 text-sm">{errors.name.message}</span>
        )}
      </div>

      <div>
        <label htmlFor="dob" className={labelClassName}>
          Date of Birth
        </label>
        <input
          id="dob"
          type="date"
          {...register('dob')}
          className={inputClassName}
        />
        {errors.dob && (
          <span className="text-red-500 text-sm">{errors.dob.message}</span>
        )}
      </div>

      <div>
        <label htmlFor="personalEmail" className={labelClassName}>
          Personal Email
        </label>
        <input
          id="personalEmail"
          type="email"
          {...register('personalEmail')}
          className={inputClassName}
        />
        {errors.personalEmail && (
          <span className="text-red-500 text-sm">
            {errors.personalEmail.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="phoneNumber" className={labelClassName}>
          Phone Number
        </label>
        <Controller
          name="phoneNumber"
          control={form.control}
          render={({ field: { onChange, value } }) => (
            <PhoneNumberInput
              defaultCountry={value?.countryCodeEnum || 'us'}
              value={value?.number}
              onChange={(phone, data) => {
                onChange?.({
                  number: phone,
                  countryCode: data.country.dialCode,
                  countryCodeEnum: data.country.iso2,
                });
              }}
            />
          )}
        />
        {errors.phoneNumber && (
          <span className="text-red-500 text-sm">
            {errors.phoneNumber.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="whatsappNumber" className={labelClassName}>
          Whatsapp Number
        </label>
        <Controller
          name="whatsappNumber"
          control={form.control}
          render={({ field: { onChange, value } }) => (
            <PhoneNumberInput
              defaultCountry={value?.countryCodeEnum || 'us'}
              value={value?.number}
              onChange={(phone, data) => {
                onChange?.({
                  number: phone,
                  countryCode: data.country.dialCode,
                  countryCodeEnum: data.country.iso2,
                });
              }}
            />
          )}
        />
        {errors.whatsappNumber && (
          <span className="text-red-500 text-sm">
            {errors.whatsappNumber.message}
          </span>
        )}
      </div>

      <div>
        <label
          htmlFor="emergencyContact.name"
          className={`${labelClassName} flex items-center gap-1`}
        >
          Emergency Contact Name{' '}
          <span className="text-red-500 text-lg mt-1">*</span>
        </label>
        <input
          id="emergencyContact.name"
          type="text"
          {...register('emergencyContact.name')}
          className={inputClassName}
          required
        />
        {errors.emergencyContact?.name && (
          <span className="text-red-500 text-sm">
            {errors.emergencyContact.name.message}
          </span>
        )}
      </div>

      <div>
        <label
          htmlFor="emergencyContact.phoneNumber"
          className={`${labelClassName} flex items-center gap-1`}
        >
          Emergency Contact Number{' '}
          <span className="text-red-500 text-lg mt-1">*</span>
        </label>
        <input
          id="emergencyContact.phoneNumber"
          type="text"
          {...register('emergencyContact.phoneNumber')}
          className={inputClassName}
          required
        />
        {errors.emergencyContact?.phoneNumber && (
          <span className="text-red-500 text-sm">
            {errors.emergencyContact.phoneNumber.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="upworkProfile" className={labelClassName}>
          Upwork Profile
        </label>
        <input
          id="upworkProfile"
          type="text"
          {...register('upworkProfile')}
          className={inputClassName}
        />
        {errors.upworkProfile && (
          <span className="text-red-500 text-sm">
            {errors.upworkProfile.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="governmentId" className={labelClassName}>
          Government ID
        </label>
        <input
          id="governmentId"
          type="text"
          {...register('governmentId')}
          className={inputClassName}
        />
        {errors.governmentId && (
          <span className="text-red-500 text-sm">
            {errors.governmentId.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="currentAddress" className={labelClassName}>
          Current Address
        </label>
        <input
          id="currentAddress"
          type="text"
          {...register('currentAddress')}
          className={inputClassName}
        />
        {errors.currentAddress && (
          <span className="text-red-500 text-sm">
            {errors.currentAddress.message}
          </span>
        )}
      </div>

      <div>
        <label
          htmlFor="permanentAddress"
          className={`${labelClassName} flex items-center gap-1`}
        >
          Permanent Address
          <span className="text-red-500 text-lg mt-1">*</span>
        </label>
        <input
          id="permanentAddress"
          type="text"
          {...register('permanentAddress')}
          className={inputClassName}
          required
        />
        {errors.permanentAddress && (
          <span className="text-red-500 text-sm">
            {errors.permanentAddress.message}
          </span>
        )}
      </div>

      <div>
        <label className={labelClassName}>Skills</label>
        <TagInput
          placeholder="Add skill and press Enter"
          onEnter={handleTagInput('skills')}
          tags={skillTags}
          onRemoveTag={handleRemoveTag('skills')}
        />
      </div>

      <div>
        <label className={labelClassName}>Hobbies (optional)</label>
        <TagInput
          placeholder="Add hobby and press Enter"
          onEnter={handleTagInput('hobbies')}
          tags={hobbyTags}
          onRemoveTag={handleRemoveTag('hobbies')}
        />
        {errors.hobbies && (
          <span className="text-red-500 text-sm">{errors.hobbies.message}</span>
        )}
      </div>

      {toolsAndChannels && (
        <div>
          <label className={labelClassName}>Tools</label>
          <Controller
            name="tools"
            control={form.control}
            render={({ field }) => (
              <SelectComponent
                {...field}
                isMulti
                options={tools}
                className="w-full"
                value={tools.filter((option) =>
                  (agent.metadata?.tools || []).includes(option.value)
                )}
                onChange={(
                  newValue: MultiValue<SelectOption> | SingleValue<SelectOption>
                ) =>
                  field.onChange(
                    (newValue as MultiValue<SelectOption>).map(
                      (item) => item.value
                    )
                  )
                }
                placeholder="Select Tools"
              />
            )}
          />
          {errors.tools && (
            <span className="text-red-500 text-sm">{errors.tools.message}</span>
          )}
        </div>
      )}

      {toolsAndChannels && (
        <div>
          <label className={labelClassName}>Channels</label>
          <Controller
            name="channels"
            control={form.control}
            render={({ field }) => (
              <SelectComponent
                {...field}
                isMulti
                options={channelOptions}
                className="w-full"
                value={channelOptions.filter((option) =>
                  (agent.metadata?.channels || []).includes(option.value)
                )}
                onChange={(
                  newValue: MultiValue<SelectOption> | SingleValue<SelectOption>
                ) =>
                  field.onChange(
                    (newValue as MultiValue<SelectOption>).map(
                      (item) => item.value
                    )
                  )
                }
                placeholder="Select Channels"
              />
            )}
          />
          {errors.channels && (
            <span className="text-red-500 text-sm">
              {errors.channels.message}
            </span>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {languageFields.fields.length > 0 && (
            <label className={labelClassName}>Languages</label>
          )}
          <button
            type="button"
            onClick={() => languageFields.append({ language: '', fluency: [] })}
            className={`flex items-center gap-1 ${
              isDarkMode
                ? 'text-white hover:text-indigo-500'
                : 'text-blue-600 hover:text-blue-800'
            }`}
          >
            <IoAddCircleOutline className="h-5 w-5" />
            {languageFields.fields.length <= 0 && 'Add Language'}
          </button>
        </div>

        {languageFields.fields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2">
            <select
              {...register(`languages.${index}.language`)}
              className={`${inputClassName} grow appearance-none`}
            >
              <option value="">Select Language</option>
              {languageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <Controller
              name={`languages.${index}.fluency`}
              control={form.control}
              render={({ field }) => (
                <SelectComponent<FluencyOption>
                  {...field}
                  isMulti
                  options={fluencyOptions}
                  className="grow w-[500px]"
                  value={fluencyOptions.filter((option) =>
                    field.value?.includes(option.value as 'Spoken' | 'Written')
                  )}
                  onChange={(
                    newValue:
                      | MultiValue<FluencyOption>
                      | SingleValue<FluencyOption>
                  ) =>
                    field.onChange(
                      (newValue as MultiValue<FluencyOption>).map(
                        (item) => item.value as 'Spoken' | 'Written'
                      )
                    )
                  }
                  placeholder="Select Fluency"
                />
              )}
            />

            <button
              type="button"
              onClick={() => languageFields.remove(index)}
              className="text-red-500 hover:text-red-700"
            >
              <FaTrashAlt className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {pastEmployersFields.fields.length > 0 && (
            <label className={labelClassName}>Past Employers</label>
          )}
          <button
            type="button"
            onClick={() => {
              pastEmployersFields.append({
                employerName: '',
                employerUrl: '',
                startDate: '',
                endDate: '',
              });
            }}
            className={`flex items-center gap-1 ${
              isDarkMode
                ? 'text-white hover:text-indigo-500'
                : 'text-blue-600 hover:text-blue-800'
            }`}
          >
            <IoAddCircleOutline className="h-5 w-5" />
            {pastEmployersFields.fields.length <= 0 && 'Add Past Employer'}
          </button>
        </div>

        {pastEmployersFields.fields.map((field, index) => (
          <div key={field.id} className="space-y-3 p-4 border rounded-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={`${labelClassName} text-xs`}>
                  Employer Name
                </label>
                <input
                  {...register(`pastEmployers.${index}.employerName`)}
                  type="text"
                  className={inputClassName}
                  placeholder="Company name"
                />
                {errors.pastEmployers?.[index]?.employerName && (
                  <span className="text-red-500 text-xs">
                    {errors.pastEmployers[index].employerName?.message}
                  </span>
                )}
              </div>

              <div>
                <label className={`${labelClassName} text-xs`}>
                  Employer URL
                </label>
                <input
                  {...register(`pastEmployers.${index}.employerUrl`)}
                  type="text"
                  className={inputClassName}
                  placeholder="https://company.com"
                />
                {errors.pastEmployers?.[index]?.employerUrl && (
                  <span className="text-red-500 text-xs">
                    {errors.pastEmployers[index].employerUrl?.message}
                  </span>
                )}
              </div>

              <div>
                <label className={`${labelClassName} text-xs`}>
                  Start Date
                </label>
                <input
                  {...register(`pastEmployers.${index}.startDate`)}
                  type="date"
                  className={inputClassName}
                />
                {errors.pastEmployers?.[index]?.startDate && (
                  <span className="text-red-500 text-xs">
                    {errors.pastEmployers[index].startDate?.message}
                  </span>
                )}
              </div>

              <div>
                <label className={`${labelClassName} text-xs`}>End Date</label>
                <input
                  {...register(`pastEmployers.${index}.endDate`)}
                  type="date"
                  className={inputClassName}
                />
                {errors.pastEmployers?.[index]?.endDate && (
                  <span className="text-red-500 text-xs">
                    {errors.pastEmployers[index].endDate?.message}
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => pastEmployersFields.remove(index)}
              className="text-red-500 text-sm hover:text-red-700 flex items-center gap-2"
            >
              <FaTrashAlt className="h-4 w-4" />
              Remove Employer
            </button>
          </div>
        ))}
      </div>

      <div>
        <label htmlFor="totalExperience" className={labelClassName}>
          Total Experience
        </label>
        <input
          id="totalExperience"
          type="text"
          {...register('totalExperience')}
          className={inputClassName}
        />
      </div>

      <div>
        <label
          htmlFor="bankDetails.name"
          className={`${labelClassName} flex items-center gap-1`}
        >
          Name as per bank <span className="text-red-500 text-lg mt-1">*</span>
        </label>
        <input
          id="bankDetails.name"
          type="text"
          {...register('bankDetails.name')}
          className={inputClassName}
          required
        />
        {errors.bankDetails?.name && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.name.message}
          </span>
        )}
      </div>

      <div>
        <label
          htmlFor="bankDetails.bankName"
          className={`${labelClassName} flex items-center gap-1`}
        >
          Bank Name <span className="text-red-500 text-lg mt-1">*</span>
        </label>
        <input
          id="bankDetails.bankName"
          type="text"
          {...register('bankDetails.bankName')}
          className={inputClassName}
          required
        />
        {errors.bankDetails?.bankName && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.bankName.message}
          </span>
        )}
      </div>

      <div>
        <label
          htmlFor="bankDetails.accountNumber"
          className={`${labelClassName} flex items-center gap-1`}
        >
          Account Number <span className="text-red-500 text-lg mt-1">*</span>
        </label>
        <input
          id="bankDetails.accountNumber"
          type="text"
          {...register('bankDetails.accountNumber')}
          className={inputClassName}
          required
        />
        {errors.bankDetails?.accountNumber && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.accountNumber.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="bankDetails.accountType" className={labelClassName}>
          Account Type <span className="text-red-500 text-lg mt-1">*</span>
        </label>
        <input
          id="bankDetails.accountType"
          type="text"
          {...register('bankDetails.accountType')}
          className={inputClassName}
          required
        />
        {errors.bankDetails?.accountType && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.accountType.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="bankDetails.bankCode" className={labelClassName}>
          Bank Code
        </label>
        <input
          id="bankDetails.bankCode"
          type="text"
          {...register('bankDetails.bankCode')}
          className={inputClassName}
        />
        {errors.bankDetails?.bankCode && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.bankCode.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="bankDetails.branchName" className={labelClassName}>
          Branch Name
        </label>
        <input
          id="bankDetails.branchName"
          type="text"
          {...register('bankDetails.branchName')}
          className={inputClassName}
        />
        {errors.bankDetails?.branchName && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.branchName.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="bankDetails.city" className={labelClassName}>
          City <span className="text-red-500 text-lg">*</span>
        </label>
        <input
          id="bankDetails.city"
          type="text"
          {...register('bankDetails.city')}
          className={inputClassName}
        />
        {errors.bankDetails?.city && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.city.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="preferredPaymentMethod" className={labelClassName}>
          Preferred Payment Method{' '}
          <span className="text-red-500 text-lg">*</span>
        </label>
        <Controller
          name="preferredPaymentMethod"
          control={form.control}
          render={({ field }) => (
            <SelectComponent<{
              value: PaymentMethod;
              label: string;
            }>
              {...field}
              value={paymentMethodOptions.find(
                (option) => option.value === field.value
              )}
              required={true}
              options={paymentMethodOptions}
              classNamePrefix="react-select"
              className="w-full"
              onChange={(
                newValue:
                  | MultiValue<{ value: PaymentMethod; label: string }>
                  | SingleValue<{ value: PaymentMethod; label: string }>
              ) =>
                field.onChange(
                  (
                    newValue as SingleValue<{
                      value: PaymentMethod;
                      label: string;
                    }>
                  )?.value
                )
              }
              placeholder="Select Payment Method"
              isMulti={false}
            />
          )}
        />
        {errors.preferredPaymentMethod && (
          <span className="text-red-500 text-sm">
            {errors.preferredPaymentMethod.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="paymentIds.paypalId" className={labelClassName}>
          PayPal ID
        </label>
        <input
          id="paymentIds.paypalId"
          type="text"
          {...register('paymentIds.paypalId')}
          className={inputClassName}
        />
        {errors.paymentIds?.paypalId && (
          <span className="text-red-500 text-sm">
            {errors.paymentIds.paypalId.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="paymentIds.payoneerId" className={labelClassName}>
          Payoneer ID
        </label>
        <input
          id="paymentIds.payoneerId"
          type="text"
          {...register('paymentIds.payoneerId')}
          className={inputClassName}
        />
        {errors.paymentIds?.payoneerId && (
          <span className="text-red-500 text-sm">
            {errors.paymentIds.payoneerId.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="paymentIds.wiseId" className={labelClassName}>
          Wise ID
        </label>
        <input
          id="paymentIds.wiseId"
          type="text"
          {...register('paymentIds.wiseId')}
          className={inputClassName}
        />
        {errors.paymentIds?.wiseId && (
          <span className="text-red-500 text-sm">
            {errors.paymentIds.wiseId.message}
          </span>
        )}
      </div>

      <div>
        <FileUpload
          title="Bank Statement *"
          files={bankStatementFiles}
          onFilesChange={handleBankStatementUpload}
          accept=".pdf,.jpg,.jpeg,.png"
          multiple={false}
          maxSize={10}
          maxFiles={1}
          isUploading={isUploadingBankStatement}
          disabled={isAddingAgent}
          fieldName="bankStatement"
        />
        {errors.bankDetails?.bankStatement && (
          <span className="text-red-500 text-sm">
            {errors.bankDetails.bankStatement.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="comments" className={labelClassName}>
          Comments
        </label>
        <textarea
          id="comments"
          {...register('comments')}
          className={inputClassName}
        />
      </div>

      <div className="flex justify-end">
        <BtnPrimary disabled={isAddingAgent} type="submit">
          {isAddingAgent ? 'Submitting...' : 'Submit'}
        </BtnPrimary>
      </div>
    </form>
  );
};

export default OnboardForm;
